/**
 * @fileoverview Global Error Handler
 * @description Captures and logs unhandled errors and promise rejections
 * @module shared/global-error-handler
 */

import { SecureLogger } from './secure-logger';

/** Benign Chromium warning from nested ResizeObserver layout updates. */
const RESIZE_OBSERVER_LOOP_PATTERN =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;

function getErrorMessage(error: unknown, fallbackMessage?: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallbackMessage ?? '';
}

function isBenignResizeObserverError(message: string): boolean {
  return RESIZE_OBSERVER_LOOP_PATTERN.test(message);
}

/**
 * Error Info Type
 * @description Contains component stack information for React errors
 */
export interface ErrorInfo {
  /** Component stack trace */
  componentStack: string;
}

/**
 * Global Error Handler
 * @class GlobalErrorHandler
 * @description Captures and logs unhandled errors and promise rejections globally.
 * Should be initialized once at application startup.
 * @example
 * ```typescript
 * GlobalErrorHandler.init();
 * ```
 */
export class GlobalErrorHandler {
  static init(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      SecureLogger.error('Unhandled Promise Rejection:', event.reason);
      event.preventDefault();
    });

    // Handle general errors
    window.addEventListener('error', (event) => {
      const message = getErrorMessage(event.error, event.message);
      if (isBenignResizeObserverError(message)) {
        event.preventDefault();
        return;
      }

      SecureLogger.error('Global Error:', event.error || event.message);
      event.preventDefault();
    });
  }

  /**
   * Handle application error
   * @param {Error} error - The error object
   * @param {ErrorInfo} errorInfo - Optional error information with component stack
   */
  static handleError(error: Error, errorInfo?: ErrorInfo): void {
    SecureLogger.error('Application Error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
    });
  }
}
